import React, { useCallback, useEffect } from 'react'
import {
    Grid,
    TextField,
    InputAdornment,
    debounce,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import SectionTitle from '../../../ui/sectionTitle';

function ValidFrameCostAndPricingForm({ formik }) {

    /*
    To improve performance, we debounce the calculations to reduce the number of times the useEffect hook is triggered.
    */
    const calculateProfit = useCallback(debounce(() => {
        try {
            const supplierCost = parseFloat(formik.values.supplierCost) || 0;
            const landingCost = parseFloat(formik.values.landingCost) || 0;
            const listPrice = parseFloat(formik.values.listPrice) || 0;
            const markupMultiplier = parseFloat(formik.values.markupMultiplier) || 1.0;

            if (supplierCost < 0 || landingCost < 0 || listPrice < 0 || markupMultiplier < 0) {
                return;
            }

            const totalCost = supplierCost + landingCost;
            const profit = (listPrice * markupMultiplier) - totalCost;
            const profitPercentage = totalCost ? (profit / totalCost) * 100 : 0;
            
            formik.setFieldValue('profit', profit.toFixed(2), false);
            formik.setFieldValue('profitPercentage', profitPercentage.toFixed(2), false);
        } catch (error) {
            console.error('Error calculating profit and profit percentage:', error);
        }
    }, 300),[formik.values.supplierCost, formik.values.landingCost, formik.values.listPrice, formik.values.markupMultiplier])

    useEffect(() => {
        calculateProfit()
    }, [calculateProfit]);

    return (
        <>
            {/* Section 2: Cost and Pricing */}
            <Grid item xs={12}>
                <SectionTitle title="Cost and Pricing" />
            </Grid>
            <Grid item xs={12} mt={1}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={formik.values.isTaxable}
                            onChange={formik.handleChange}
                            name="isTaxable"
                            color="primary"
                        />
                    }
                    label="Is Taxable"
                />
            </Grid>
            <Grid item xs={12} mt={1}>
                <TextField
                    value={formik.values.defaultTaxPercentage}
                    onChange={formik.handleChange}
                    name="defaultTaxPercentage"
                    label="Default Tax %"
                    type="text"
                    size='small'
                    fullWidth
                    InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                // Add validation if needed
                />
            </Grid>
            <Grid item xs={12} mt={2}>
                <TextField
                    value={formik.values.supplierCost}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    id='supplierCost'
                    name="supplierCost"
                    label="Supplier Cost"
                    type="text"
                    size='small'
                    fullWidth
                    InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    error={formik.touched.supplierCost && Boolean(formik.errors.supplierCost)} // Add error prop
                    helperText={formik.touched.supplierCost && formik.errors.supplierCost}
                />
            </Grid>

            <Grid item xs={12} mt={1}>
                <TextField
                    value={formik.values.landingCost}
                    onChange={formik.handleChange}
                    name="landingCost"
                    label="Landing Cost"
                    type="text"
                    size='small'
                    fullWidth
                    InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    error={formik.touched.landingCost && Boolean(formik.errors.landingCost)} // Add error prop
                    helperText={formik.touched.landingCost && formik.errors.landingCost}
                />
            </Grid>
            <Grid item xs={12} mt={1}>
                <TextField
                    value={formik.values.listPrice}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="listPrice"
                    label="List Price"
                    type="text"
                    size='small'
                    required
                    fullWidth
                    InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    error={formik.touched.listPrice && Boolean(formik.errors.listPrice)} // Add error prop
                    helperText={formik.touched.listPrice && formik.errors.listPrice}
                />
            </Grid>
            <Grid item xs={12} mt={1}>
                <TextField
                    value={formik.values.markupMultiplier}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="markupMultiplier"
                    label="Markup Multiplier"
                    type="text"
                    size='small'
                    fullWidth
                    error={formik.touched.markupMultiplier && Boolean(formik.errors.markupMultiplier)} // Add error prop
                    helperText={formik.touched.markupMultiplier && formik.errors.markupMultiplier}
                />
            </Grid>
            <Grid item xs={12} mt={1}>
                <TextField
                    value={formik.values.profit}
                    onChange={formik.handleChange}
                    name="profit"
                    label="Profit"
                    type="text"
                    size='small'
                    disabled
                    fullWidth
                    InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                />
            </Grid>
            <Grid item xs={12} mt={1}>
                <TextField
                    value={formik.values.profitPercentage}
                    name="profitPercentage"
                    label="Profit Percentage"
                    size='small'
                    fullWidth
                    disabled
                    InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                />
            </Grid>
        </>
    )
}

export default ValidFrameCostAndPricingForm